import { useState } from 'react'

import { ReportCreatePageQuery } from 'generated/resources/graphql'
import { isEqual } from 'lodash'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { ReportSchemaType } from 'modules/ai_reports/lib/types'

/*
 * NOTE: Schemas are difficult to manage.
 * Schema is necessary for different things, one is showing the appropriate
 * columns, another one is building the available filters.
 * The problem is that there's not just one schema but two: one is the schema
 * of the table, and another one is the schema of the query.
 * Both are important: for the available columns you need the query schema,
 * for the available filters you need a merge of both, and what's worse,
 * they might overlap.
 *
 * Currently we don't do any attempts to do the merge. Furthermore, there's a
 * problem in the Data Collection where changing the available filters will
 * not be reflected in the UI.
 */
export const useReportSchema = (
  irisTableColumns: NonNullable<
    ReportCreatePageQuery['iris']['factTable']
  >['columnsConnection']['nodes']
) => {
  const [schema, setSchema] = useState<ReportSchemaType>([])
  const handleSchemaChanged = useUnsafeCallback(
    (receivedSchema: ReportSchemaType) => {
      // Prevent infinite re-fetches of the data collection
      if (!isEqual(receivedSchema, schema)) setSchema(receivedSchema)
    },
    [schema]
  )
  useUnsafeEffect(() => {
    handleSchemaChanged(irisTableColumns.map((node) => node.schema))
  }, [irisTableColumns])

  return [schema, handleSchemaChanged] as const
}
